import { useState, useRef, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, X, ShieldCheck, Zap, Copy, Check, RefreshCw, Trophy, KeyRound, Loader2 } from "lucide-react";

const OTP_TTL = 60;

export default function RegistrationModal({ open, onClose, onComplete }) {
  /**
   * Steps:
   *   "choose"      — New here? or Already registered?
   *   "battle-otp"  — returning user enters their 5-digit Battle OTP
   *   "resend"      — forgot OTP: enter phone → resend to email
   *   "new-form"    — new user fills name + email + phone
   *   "reg-otp"     — new user enters the one-time verification OTP
   *   "done"        — success screen
   */
  const [step, setStep]       = useState("choose");
  const [battleOtp, setBattleOtp] = useState(["", "", "", "", ""]);
  const [logging, setLogging] = useState(false);
  const [form, setForm]       = useState({ name: "", email: "", phone: "" });
  const [regOtp, setRegOtp]   = useState(["", "", "", "", ""]);
  const [devOtp, setDevOtp]   = useState(null);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [countdown, setCountdown] = useState(OTP_TTL);
  const [copied, setCopied]   = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [tournamentInfo, setTournamentInfo] = useState(null);
  const [entryCode, setEntryCode] = useState(null);
  const battleRefs = useRef([]);
  const regRefs    = useRef([]);
  const timerRef   = useRef(null);

  const [resendPhone, setResendPhone] = useState("");
  const [resending, setResending] = useState(false);

  const reset = useCallback(() => {
    setStep("choose");
    setBattleOtp(["", "", "", "", ""]);
    setForm({ name: "", email: "", phone: "" });
    setRegOtp(["", "", "", "", ""]);
    setDevOtp(null);
    setTournamentInfo(null);
    setEntryCode(null);
    setResendPhone("");
    clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    if (!open) { reset(); return; }
    return () => clearInterval(timerRef.current);
  }, [open, reset]);

  // ── Must be defined before early return (Rules of Hooks) ─────────────────
  const startCountdown = useCallback(() => {
    setCountdown(OTP_TTL);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((c) => { if (c <= 1) { clearInterval(timerRef.current); return 0; } return c - 1; });
    }, 1000);
  }, []);

  if (!open) return null;

  // ── Battle OTP quick-login ────────────────────────────────────────────────
  const handleBattleOtpChange = (i, v) => {
    const ch = v.replace(/\D/g, "").slice(-1);
    const next = [...battleOtp]; next[i] = ch; setBattleOtp(next);
    if (ch && i < 4) battleRefs.current[i + 1]?.focus();
  };
  const handleBattleOtpKey = (i, e) => {
    if (e.key === "Backspace" && !battleOtp[i] && i > 0) battleRefs.current[i - 1]?.focus();
  };
  const handleBattleOtpPaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 5);
    if (!txt) return;
    e.preventDefault();
    const arr = ["", "", "", "", ""];
    for (let i = 0; i < txt.length; i++) arr[i] = txt[i];
    setBattleOtp(arr);
    battleRefs.current[Math.min(txt.length, 4)]?.focus();
  };

  const loginWithBattleOtp = async () => {
    const code = battleOtp.join("");
    if (code.length < 5) { toast.error("Enter all 5 digits of your Battle OTP"); return; }
    setLogging(true);
    try {
      const r = await api.userQuickLogin(code);
      const u = { name: r.name, email: r.email, phone: r.phone };
      toast.success(`Welcome back, ${r.name.split(" ")[0]}!`);
      onComplete(u);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OTP not recognised. Use 'Forgot OTP' to resend it.");
    } finally { setLogging(false); }
  };

  const resendBattleOtp = async () => {
    const phone = resendPhone.trim();
    if (!phone) { toast.error("Enter your registered phone number"); return; }
    setResending(true);
    try {
      await api.userResendBattleEntry(phone);
      toast.success("Your Battle OTP has been sent to your registered email!");
      setBattleOtp(["", "", "", "", ""]);
      setStep("battle-otp");
      setTimeout(() => battleRefs.current[0]?.focus(), 80);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Phone not found. Please register as a new player.");
    } finally { setResending(false); }
  };

  // ── New user registration flow ────────────────────────────────────────────
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const sendOtp = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) { toast.error("Please fill all fields"); return; }
    if (form.phone.replace(/\D/g, "").length < 7) { toast.error("Enter a valid phone number"); return; }
    setSending(true);
    try {
      const r = await api.otpSend(form);
      const code = r.dev_otp || null;
      setDevOtp(code);
      if (code) {
        const arr = code.split("").slice(0, 5);
        while (arr.length < 5) arr.push("");
        setRegOtp(arr);
      }
      startCountdown();
      setStep("reg-otp");
      setTimeout(() => regRefs.current[4]?.focus(), 80);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate OTP");
    } finally { setSending(false); }
  };

  const resendOtp = async () => {
    if (countdown > 0) return;
    setSending(true);
    try {
      const r = await api.otpSend(form);
      const code = r.dev_otp || null;
      setDevOtp(code);
      if (code) { const arr = code.split("").slice(0, 5); while (arr.length < 5) arr.push(""); setRegOtp(arr); }
      startCountdown();
      toast.success("New OTP generated");
    } catch { toast.error("Failed to regenerate OTP"); }
    finally { setSending(false); }
  };

  const handleRegOtpChange = (i, v) => {
    const ch = v.replace(/\D/g, "").slice(-1);
    const next = [...regOtp]; next[i] = ch; setRegOtp(next);
    if (ch && i < 4) regRefs.current[i + 1]?.focus();
  };
  const handleRegOtpKey = (i, e) => { if (e.key === "Backspace" && !regOtp[i] && i > 0) regRefs.current[i - 1]?.focus(); };
  const handleRegOtpPaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 5);
    if (!txt) return;
    e.preventDefault();
    const arr = ["", "", "", "", ""];
    for (let i = 0; i < txt.length; i++) arr[i] = txt[i];
    setRegOtp(arr);
    regRefs.current[Math.min(txt.length, 4)]?.focus();
  };

  const verify = async () => {
    const code = regOtp.join("");
    if (code.length < 5) { toast.error("Enter the 5-digit code"); return; }
    setVerifying(true);
    try {
      const r = await api.otpVerify({ ...form, otp: code });
      toast.success(`Verified, ${r.name.split(" ")[0]}!`);
      setTournamentInfo(r.tournament || null);
      setEntryCode(r.entry_code || null);
      const u = { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() };
      setStep("done");
      onComplete(u);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid OTP");
    } finally { setVerifying(false); }
  };

  const timerColor = countdown > 30 ? "text-emerald-400" : countdown > 10 ? "text-yellow-400" : "text-[color:var(--kl-red)]";
  const timerBg    = countdown > 30 ? "bg-emerald-500/10 border-emerald-500/30" : countdown > 10 ? "bg-yellow-500/10 border-yellow-500/30" : "bg-red-500/10 border-red-500/30";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      data-testid="register-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="form-dark w-full max-w-xl rounded-md border border-[color:var(--kl-navy-line)] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] slide-up overflow-hidden"
        style={{ background: "linear-gradient(180deg,#0a0f2c 0%, #060a1f 100%)" }}
      >
        {/* header */}
        <div className="flex items-center justify-between px-7 py-5 border-b border-[color:var(--kl-navy-line)]">
          <span className="pill-tag text-white/80"><span className="dot" /> Proctorly · Blitz</span>
          <button onClick={onClose} data-testid="register-close" className="text-white/60 hover:text-white p-1 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-7 md:px-10 pt-7 pb-9">

          {/* ── STEP: Choose ──────────────────────────────────────────── */}
          {step === "choose" && (
            <>
              <h2 className="text-white text-3xl font-bold leading-tight mb-2">
                Welcome to <span className="proctorly-mark">Proctorly AI</span>
              </h2>
              <p className="text-white/45 text-sm mb-7">First time here or have you registered before?</p>

              <div className="space-y-3">
                <button
                  onClick={() => { setStep("battle-otp"); setTimeout(() => battleRefs.current[0]?.focus(), 80); }}
                  className="w-full rounded-md border border-[color:var(--kl-red)]/40 bg-[color:var(--kl-red)]/8 hover:bg-[color:var(--kl-red)]/15 transition p-5 text-left flex items-center gap-4"
                >
                  <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: "rgba(230,57,70,0.15)", border: "1px solid rgba(230,57,70,0.4)" }}>
                    <KeyRound className="w-4 h-4 text-[color:var(--kl-red)]" />
                  </div>
                  <div>
                    <div className="text-white font-bold">Already Registered</div>
                    <div className="text-white/45 text-xs mt-0.5">Sign in with my 5-digit Battle OTP</div>
                  </div>
                </button>

                <button
                  onClick={() => setStep("new-form")}
                  className="w-full rounded-md border border-white/15 bg-white/3 hover:bg-white/7 transition p-5 text-left flex items-center gap-4"
                >
                  <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.2)" }}>
                    <ArrowUpRight className="w-4 h-4 text-white/60" />
                  </div>
                  <div>
                    <div className="text-white font-bold">New Here</div>
                    <div className="text-white/45 text-xs mt-0.5">Register in under a minute</div>
                  </div>
                </button>
              </div>
            </>
          )}

          {/* ── STEP: Battle OTP quick-login ──────────────────────────── */}
          {step === "battle-otp" && (
            <>
              <h2 className="text-white text-2xl md:text-3xl font-bold leading-tight mb-1">
                Enter your <span className="proctorly-mark">Battle OTP</span>
              </h2>
              <p className="text-white/45 text-sm mb-5">The 5-digit code from your registration email.</p>

              <div className="flex gap-2.5 justify-between">
                {battleOtp.map((d, i) => (
                  <input key={i} ref={(el) => (battleRefs.current[i] = el)}
                    data-testid={`botp-${i}`} inputMode="numeric" maxLength={1} value={d}
                    onChange={(e) => handleBattleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleBattleOtpKey(i, e)}
                    onPaste={i === 0 ? handleBattleOtpPaste : undefined}
                    className="otp-dark flex-1" />
                ))}
              </div>

              <button onClick={loginWithBattleOtp} disabled={logging || battleOtp.join("").length < 5}
                data-testid="battle-otp-login"
                className="btn-submit w-full justify-center mt-5 disabled:opacity-40 disabled:cursor-not-allowed">
                {logging ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Verifying…</> : <>Sign In <Zap className="w-4 h-4" /></>}
              </button>

              <div className="mt-4 text-center">
                <button onClick={() => setStep("resend")}
                  className="font-mono-kl text-[10px] uppercase tracking-widest text-[color:var(--kl-red)]/70 hover:text-[color:var(--kl-red)] transition">
                  Don't have the code? Resend it →
                </button>
              </div>
              <button onClick={() => setStep("choose")} className="block mx-auto mt-3 font-mono-kl text-[10px] uppercase tracking-widest text-white/25 hover:text-white/50 transition">← Back</button>
            </>
          )}

          {/* ── STEP: Resend Battle OTP ───────────────────────────────── */}
          {step === "resend" && (
            <>
              <h2 className="text-white text-2xl font-bold leading-tight mb-1">Resend <span className="proctorly-mark">Battle OTP</span></h2>
              <p className="text-white/45 text-sm mb-5">Enter your registered phone. We'll email your code.</p>

              <label className="form-label">Registered Phone <span className="req">*</span></label>
              <input className="input-dark mb-5" value={resendPhone}
                onChange={(e) => setResendPhone(e.target.value)}
                placeholder="+91 98xxxxxxxx" autoComplete="tel" />

              <div className="flex items-center justify-between">
                <button onClick={() => setStep("battle-otp")} className="btn-ghost-dark text-xs">← Back</button>
                <button onClick={resendBattleOtp} disabled={resending} className="btn-submit">
                  {resending ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Sending…</> : <>Send Code <ArrowUpRight className="w-4 h-4" /></>}
                </button>
              </div>
            </>
          )}

          {/* ── STEP: New user full form ───────────────────────────────── */}
          {step === "new-form" && (
            <>
              <h2 className="text-white text-3xl md:text-[2.6rem] font-bold leading-[1.05] tracking-tight">
                Quick <span className="proctorly-mark">registration.</span>
              </h2>
              <p className="text-white/55 mt-2 text-sm">One-time only. You'll get a personal OTP to sign in anytime after.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-7">
                <div className="md:col-span-2">
                  <label className="form-label">Full Name <span className="req">*</span></label>
                  <input data-testid="reg-name" className="input-dark" value={form.name} onChange={(e) => setF("name", e.target.value)} placeholder="Ada Lovelace" autoComplete="name" />
                </div>
                <div>
                  <label className="form-label">Email <span className="req">*</span></label>
                  <input data-testid="reg-email" type="email" className="input-dark" value={form.email} onChange={(e) => setF("email", e.target.value)} placeholder="you@example.com" autoComplete="email" />
                </div>
                <div>
                  <label className="form-label">Phone <span className="req">*</span></label>
                  <input data-testid="reg-phone" className="input-dark" value={form.phone} onChange={(e) => setF("phone", e.target.value)} placeholder="+91 98xxxxxxxx" autoComplete="tel" />
                </div>
              </div>

              <div className="flex items-center justify-between gap-4 mt-7">
                <button onClick={() => setStep("choose")} className="btn-ghost-dark text-sm">← Back</button>
                <button onClick={sendOtp} disabled={sending} data-testid="reg-send-otp" className="btn-submit">
                  {sending ? "Generating…" : "Get OTP"} <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </>
          )}

          {/* ── STEP: Verification OTP ─────────────────────────────────── */}
          {step === "reg-otp" && (
            <>
              <h2 className="text-white text-3xl md:text-[2.4rem] font-bold leading-[1.05] tracking-tight">
                Your <span className="proctorly-mark">OTP</span> is here.
              </h2>
              <p className="text-white/55 mt-2 text-sm">Phone: <span className="text-white font-semibold">{form.phone}</span></p>

              {devOtp && (
                <div className="mt-5 rounded-lg border border-[color:var(--kl-red)]/50 bg-[color:var(--kl-red)]/10 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-mono-kl text-[11px] uppercase tracking-widest text-[color:var(--kl-red)]">
                      Verification OTP — valid for {countdown}s
                    </span>
                    <span className={`font-mono-kl text-xs px-2 py-0.5 rounded border font-bold tabular-nums ${timerBg} ${timerColor}`}>{countdown}s</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span data-testid="dev-otp" className="font-display text-5xl tracking-[0.3em] text-white">{devOtp}</span>
                    <button
                      onClick={() => { navigator.clipboard.writeText(devOtp).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); }}
                      className="flex items-center gap-2 px-3 py-2 rounded border border-white/20 text-white/70 hover:text-white hover:border-white/40 transition text-sm font-mono-kl"
                    >
                      {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                      {copied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                  {countdown === 0 && (
                    <button onClick={resendOtp} disabled={sending} className="mt-3 flex items-center gap-2 text-[color:var(--kl-red)] text-sm font-mono-kl uppercase tracking-widest hover:opacity-80 transition">
                      <RefreshCw className="w-3.5 h-3.5" /> Generate new OTP
                    </button>
                  )}
                </div>
              )}

              <div className="flex gap-2.5 justify-between mt-6">
                {regOtp.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => (regRefs.current[i] = el)}
                    data-testid={`otp-${i}`}
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={(e) => handleRegOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleRegOtpKey(i, e)}
                    onPaste={handleRegOtpPaste}
                    className="otp-dark flex-1"
                  />
                ))}
              </div>

              <div className="flex items-center justify-between gap-3 mt-7">
                <button onClick={() => setStep("new-form")} className="btn-ghost-dark" data-testid="otp-back">← Back</button>
                <button onClick={verify} disabled={verifying} className="btn-submit" data-testid="otp-verify">
                  {verifying ? "Verifying…" : "Verify & Enter"} <Zap className="w-4 h-4" />
                </button>
              </div>
            </>
          )}

          {/* ── STEP: Done — show Battle OTP prominently ──────────────── */}
          {step === "done" && (
            <div className="flex flex-col items-center text-center gap-5 py-4">
              <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                <ShieldCheck className="w-8 h-8 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-white text-3xl font-bold leading-tight">
                  You're in, <span className="proctorly-mark">{form.name.split(" ")[0]}</span>!
                </h2>
                <p className="text-white/55 mt-2 text-sm">Registration complete.</p>
              </div>

              {/* Battle OTP — save this! */}
              {entryCode && (
                <div className="w-full rounded-lg border border-[color:var(--kl-red)]/50 bg-[color:var(--kl-red)]/10 p-5 text-left">
                  <div className="flex items-center gap-2 mb-1">
                    <KeyRound className="w-4 h-4 text-[color:var(--kl-red)]" />
                    <span className="font-mono-kl text-[11px] uppercase tracking-widest text-[color:var(--kl-red)]">
                      Your Personal Battle OTP — Save this!
                    </span>
                  </div>
                  <p className="text-white/45 text-xs mb-3">
                    Use these 5 digits to sign in instantly on any device. Valid for 8 hours.
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="font-display text-5xl tracking-[0.4em] text-white" data-testid="entry-code">{entryCode}</span>
                    <button
                      onClick={() => { navigator.clipboard.writeText(entryCode).then(() => { setCodeCopied(true); setTimeout(() => setCodeCopied(false), 2000); }); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-white/20 text-white/60 hover:text-white transition text-xs font-mono-kl"
                    >
                      {codeCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                      {codeCopied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                </div>
              )}

              {tournamentInfo && (
                <div className="w-full rounded-lg border border-[color:var(--kl-red)]/40 bg-[color:var(--kl-red)]/10 p-5 text-left">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="w-4 h-4 text-[color:var(--kl-red)]" />
                    <span className="font-mono-kl text-[11px] uppercase tracking-widest text-[color:var(--kl-red)]">Tournament Assigned</span>
                  </div>
                  <p className="text-white font-bold">{tournamentInfo.name}</p>
                </div>
              )}

              <button onClick={onClose} className="btn-submit w-full">
                Done <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
